import React, { useMemo, useState, useEffect } from 'react';
import { useWebSocketContext } from '../context/Theme';
import { MessageCircle, X, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { trackChatEvent } from '../utils/analytics';
import MobileCarSearch from './New Chatbody/MobileCarSearch';
import PromotionsSearch from './New Chatbody/PromotionsSearch';
import ServiceSearch from './New Chatbody/ServiceSearch';
import RentalSearch from './New Chatbody/RentalSearch';
import DefaultChat from './New Chatbody/DefaultChat';
import pushNotificationManager from '../utils/pushNotifications';
import { createTranslator, getBrowserLanguage } from '../utils/i18n';
import { getFixedUrlChatCtaKind } from '../utils/urlPatterns';

import './NewToggleChat.css';

/** First-visit welcome bubble: once per session (SPA) + permanent ack so it does not repeat every navigation. */
const LS_WELCOME_ACK = 'mia_welcome_chat_cta_ack';
const SS_WELCOME_SESSION = 'mia_welcome_chat_cta_session';

function getWelcomeAck() {
  try {
    return localStorage.getItem(LS_WELCOME_ACK) === '1';
  } catch {
    return true;
  }
}

function acknowledgeWelcomeCta() {
  try {
    localStorage.setItem(LS_WELCOME_ACK, '1');
  } catch { /* ignore */ }
}

function getWelcomeSessionShown() {
  try {
    return sessionStorage.getItem(SS_WELCOME_SESSION) === '1';
  } catch {
    return true;
  }
}

function markWelcomeSessionShown() {
  try {
    sessionStorage.setItem(SS_WELCOME_SESSION, '1');
  } catch { /* ignore */ }
}

const URL_SOURCE_TO_SESSION_KIND = {
  'url-promozioni': 'promozioni',
  'url-officina': 'officina',
  'url-stock-usate': 'usate',
  'url-stock-nuove': 'nuove',
};

const SS_URL_CTA_ACK = 'mia_url_cta_ack_';

const URL_CTA_BODY_KEYS = {
  'url-promozioni': 'urlCtaPromozioniBody',
  'url-officina': 'urlCtaOfficinaBody',
  'url-stock-usate': 'urlCtaStockUsateBody',
  'url-stock-nuove': 'urlCtaStockNuoveBody',
  'scroll-engage': 'scrollEngageBody',
};

const URL_CTA_ACTION_KEYS = {
  'url-promozioni': 'urlCtaPromozioniAction',
  'url-officina': 'urlCtaOfficinaAction',
  'url-stock-usate': 'urlCtaStockUsateAction',
  'url-stock-nuove': 'urlCtaStockNuoveAction',
};

function getUrlCtaAck(kind) {
  try {
    return sessionStorage.getItem(SS_URL_CTA_ACK + kind) === '1';
  } catch {
    return true;
  }
}

function acknowledgeUrlCtaBySource(source) {
  const kind = URL_SOURCE_TO_SESSION_KIND[source];
  if (!kind) return;
  try {
    sessionStorage.setItem(SS_URL_CTA_ACK + kind, '1');
  } catch { /* ignore */ }
}

function getUrlCtaAckForSource(source) {
  const kind = URL_SOURCE_TO_SESSION_KIND[source];
  if (!kind) return true;
  return getUrlCtaAck(kind);
}

function usePathnameSync() {
  const [pathname, setPathname] = useState(
    () => (typeof window !== 'undefined' ? window.location.pathname : '')
  );

  useEffect(() => {
    const sync = () => setPathname(window.location.pathname);
    sync();
    window.addEventListener('popstate', sync);
    window.addEventListener('hashchange', sync);
    const h = window.history;
    const origPush = h.pushState.bind(h);
    const origReplace = h.replaceState.bind(h);
    h.pushState = function pushStatePatched(...args) {
      origPush(...args);
      sync();
    };
    h.replaceState = function replaceStatePatched(...args) {
      origReplace(...args);
      sync();
    };
    const interval = window.setInterval(sync, 1200);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('hashchange', sync);
      h.pushState = origPush;
      h.replaceState = origReplace;
      window.clearInterval(interval);
    };
  }, []);

  return pathname;
}

function getTimeOfDayGreeting(t) {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 13) return t('goodMorning');
  if (hour >= 13 && hour < 18) return t('goodAfternoon');
  return t('goodEvening');
}

function getFixedFlutterActionLabel(t, source) {
  if (source === 'welcome') return getTimeOfDayGreeting(t);
  if (source === 'scroll-engage') return t('scrollEngageAction');
  const actionKey = URL_CTA_ACTION_KEYS[source];
  if (actionKey) return t(actionKey);
  return t('clickToStart');
}

/** User message sent when the flutter CTA is clicked (same flow as notification + WebSocket bubble). */
function getOutgoingMessageForFixedCta(source, t, websocketCtaText) {
  if (source === 'websocket') return (websocketCtaText || '').trim();
  if (source === 'welcome') return t('fixedCtaOutgoingWelcome');
  if (source === 'url-promozioni') return t('fixedCtaOutgoingUrlPromozioni');
  if (source === 'url-officina') return t('fixedCtaOutgoingUrlOfficina');
  if (source === 'url-stock-usate') return t('fixedCtaOutgoingUrlUsate');
  if (source === 'url-stock-nuove') return t('fixedCtaOutgoingUrlNuove');
  if (source === 'scroll-engage') return t('fixedCtaOutgoingScrollEngage');
  return '';
}

/** Gradient + pill action: only fixed CTAs (welcome, URL patterns, scroll-engage). WebSocket keeps the original plain white card. */
function isEnhancedFlutterCardSource(source) {
  if (!source || source === 'websocket') return false;
  if (source === 'welcome' || source === 'scroll-engage') return true;
  return String(source).startsWith('url-');
}

const SS_SCROLL_ENGAGE_ACK = 'mia_scroll_engage_ack';

function useScrollDepthTrigger(enabled) {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (!enabled || triggered) return;

    try {
      if (sessionStorage.getItem(SS_SCROLL_ENGAGE_ACK) === '1') {
        setTriggered(false);
        return;
      }
    } catch { /* ignore */ }

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = Math.max(
          document.documentElement.scrollHeight,
          document.body.scrollHeight
        );
        const viewportHeight = window.innerHeight;
        const scrollableHeight = docHeight - viewportHeight;
        if (scrollableHeight <= 0) return;
        const pct = scrollTop / scrollableHeight;
        if (pct >= 0.55) {
          setTriggered(true);
          try { sessionStorage.setItem(SS_SCROLL_ENGAGE_ACK, '1'); } catch { /* ignore */ }
          window.removeEventListener('scroll', onScroll, true);
        }
      });
    };

    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [enabled, triggered]);

  return triggered;
}

const NewToggleChat = ({ isChatVisible, toggleChat, onSendMessage, onPopulateInput, notification, chatBodyRef }) => {
  // State variables
  const [floatingMessages, setFloatingMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [showMessage, setShowMessage] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [lastSeenNotificationId, setLastSeenNotificationId] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false); // Animation lock state
  const [isClosing, setIsClosing] = useState(false);
  const [backendCta, setBackendCta] = useState({
    text: "",
    visible: false,
    isEaten: false,
    /** `websocket` | `welcome` | `url-*` | null */
    source: null
  });
  /** Mobile: after 5s idle (or on first chat interaction) hide the blue "Chatta" pill so only the FAB anchor shows — avoids covering host bottom nav. */
  const [mobileFabOnly, setMobileFabOnly] = useState(false);
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  const pathname = usePathnameSync();
  /** Scroll-depth re-engagement: fire once per session when user scrolls past 55% and no CTA is currently visible. */
  const scrollEngageReady = useScrollDepthTrigger(!isChatVisible && !backendCta.visible && !showMessage);

  const isMobile = windowWidth <= 640;
  /** Previously the text CTA was hidden below 480px, leaving only the logo — very low intent clarity on phones. */
  const isNarrowViewport = windowWidth <= 480;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileFabOnly(false);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || isChatVisible || mobileFabOnly) return;
    const t = setTimeout(() => setMobileFabOnly(true), 5000);
    return () => clearTimeout(t);
  }, [isMobile, isChatVisible, mobileFabOnly]);



  // Handle notifications
  useEffect(() => {
    // useEffect triggered
    if (notification && !isChatVisible) {
      // Processing notification
      
      const notificationId = notification.id || 
                            notification.timestamp || 
                            `${notification.message_to_send || notification.custom_message}_${Date.now()}`;
      
      if (notificationId !== lastSeenNotificationId) {
        // Show floating messages on both desktop and mobile
        const notificationMessage = {
          id: notificationId,
          // `text` = message to send on click, `message` = user-visible copy
          text: notification.message_to_send || notification.custom_message || "",
          message: notification.visible_message || notification.message || "",
          cta_label: notification.cta_label || "",
          type: notification.type,
          duration: notification.duration
        };
        
        // Setting notification message
        
        setFloatingMessages([notificationMessage]);
        setCurrentMessageIndex(0);
        setShowMessage(true);
        
        if (notification.duration) {
          const timer = setTimeout(() => {
            // Hiding notification after duration
            setFloatingMessages([]);
            setShowMessage(false);
          }, notification.duration * 1000); // Convert seconds to milliseconds
          
          return () => {
            clearTimeout(timer);
          };
        }
      }
    }
  }, [notification, isChatVisible, lastSeenNotificationId, isMobile]);

  // Handle chat visibility changes
  useEffect(() => {
    if (isChatVisible) {
      setShowMessage(false);
      
      if (floatingMessages.length > 0 && floatingMessages[currentMessageIndex]) {
        setLastSeenNotificationId(floatingMessages[currentMessageIndex].id || 
                               `${floatingMessages[currentMessageIndex].text}_${Date.now()}`);
      }
    }
  }, [isChatVisible, floatingMessages, currentMessageIndex]);



  const dismissFixedCtaIfOpen = () => {
    setBackendCta(prev => {
      if (prev.source === 'welcome') {
        acknowledgeWelcomeCta();
        return { ...prev, visible: false, isEaten: false };
      }
      if (prev.source === 'scroll-engage') {
        return { ...prev, visible: false, isEaten: false };
      }
      if (prev.source?.startsWith('url-')) {
        acknowledgeUrlCtaBySource(prev.source);
        return { ...prev, visible: false, isEaten: false };
      }
      return prev;
    });
  };

  const collapseMobileChatta = () => {
    if (isMobile) setMobileFabOnly(true);
  };

  // Handle toggle click
  const handleToggleClick = async () => {
    // Skip if already animating
    if (isAnimating) return;

    collapseMobileChatta();
    dismissFixedCtaIfOpen();
    
    setIsAnimating(true);
    
    if (showMessage) {
      // If message is showing but chat is not open yet
      setShowMessage(false);
      
      if (floatingMessages.length > 0 && floatingMessages[currentMessageIndex]) {
        setLastSeenNotificationId(floatingMessages[currentMessageIndex].id || 
                             `${floatingMessages[currentMessageIndex].text}_${Date.now()}`);
      }
    }
    
    trackChatEvent.toggleChat(true);
    
    // Always ensure the chat opens in full screen when clicked
    // We're manually toggling, so openedByTrigger should be false
    toggleChat(false);
    
    // Request push notifications if this is the first time opening chat
    // Do this asynchronously without blocking the chat opening
    setTimeout(async () => {
      try {

        
        if (!pushNotificationManager.isInitialized) {
          return;
        }
        
        if (chatId) {
          const apiToken = localStorage.getItem("api_token");
          
          if (apiToken) {
            // Set chat context
            pushNotificationManager.setChatContext(chatId, apiToken);
            
            // Request push notifications (only asks first time)
            await pushNotificationManager.setupPushNotifications();
          }
        }
      } catch (error) {
        // Handle push notification errors gracefully
        console.error('Push notification setup error:', error);
        if (error.message) {
          console.error('Error message:', error.message);
        }
      }
    }, 2000); // Increased delay to ensure API initialization is complete
    
    // Reset animation lock after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Overlay search submission handlers removed - now using inline search within ChatBody

  // Handle message click
  const handleMessageClick = (message) => {
    if (message.onClick) {
      message.onClick();
    } else if (message.message || message.text) {
      collapseMobileChatta();
      trackChatEvent.toggleChat(true);
      toggleChat();
      
      setTimeout(() => {
        // Prefer sending the prebuilt "message_to_send" (text) to start the chat.
        onSendMessage(message.text || message.message);
      }, 300);
      
      if (floatingMessages.length > 0 && floatingMessages[currentMessageIndex]) {
        setLastSeenNotificationId(floatingMessages[currentMessageIndex].id || 
                              `${floatingMessages[currentMessageIndex].text}_${Date.now()}`);
      }
      
      setShowMessage(false);
    }
  };

  // Handle closing message
  const handleCloseMessage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMessage(false);
    setFloatingMessages([]);
  };

  // Get display message
  const getDisplayMessage = () => {
    const currentMessage = floatingMessages[currentMessageIndex];
    // Prefer the user-visible copy (visible_message) for display.
    const displayText = currentMessage?.message || currentMessage?.text || "";
    // Getting display message
    return displayText;
  };



  // Monitor the chat visibility changes
  useEffect(() => {
    if (isAnimating && !isChatVisible) {
      // We're animating and chat is now hidden, so we're in a closing transition
      setIsClosing(true);
      
      // Reset the closing state after animation completes
      const timer = setTimeout(() => {
        setIsClosing(false);
        setIsAnimating(false);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isAnimating, isChatVisible]);

  // Access WebSocket connection status and chat data
  const { isConnected, colors, chatId } = useWebSocketContext();

  const openBackendCtaChat = () => {
    const outgoing = getOutgoingMessageForFixedCta(
      backendCta.source,
      t,
      colors?.websocketData?.cta || ''
    );
    const isWebsocket = backendCta.source === 'websocket';
    collapseMobileChatta();
    dismissFixedCtaIfOpen();
    toggleChat(false);
    if (outgoing) {
      if (isWebsocket && onSendMessage) {
        setTimeout(() => onSendMessage(outgoing), 300);
      } else if (onPopulateInput) {
        setTimeout(() => onPopulateInput(outgoing), 300);
      }
    }
  };
  
  // Welcome / URL fixed CTAs: 5s auto-hide. WebSocket CTA: 10s on mobile only.
  useEffect(() => {
    if (!backendCta.visible) return;

    const src = backendCta.source;
    const isShortDismiss =
      src === 'welcome' || src === 'scroll-engage' || (typeof src === 'string' && src.startsWith('url-'));
    if (!isShortDismiss && !isMobile) return;

    const eatDelayMs = isShortDismiss ? 5000 : 10000;
    const hideDelayMs = eatDelayMs + 600;

    const eatTimer = setTimeout(() => {
      setBackendCta(prev => ({ ...prev, isEaten: true }));
    }, eatDelayMs);

    const hideTimer = setTimeout(() => {
      setBackendCta(prev => {
        if (prev.source === 'welcome') {
          acknowledgeWelcomeCta();
        } else if (prev.source?.startsWith('url-')) {
          acknowledgeUrlCtaBySource(prev.source);
        }
        return { ...prev, visible: false, isEaten: false };
      });
    }, hideDelayMs);

    return () => {
      clearTimeout(eatTimer);
      clearTimeout(hideTimer);
    };
  }, [isMobile, backendCta.visible, backendCta.source]);

  // WebSocket CTA (priority) → URL-pattern fixed CTAs → first-visit welcome.
  // Guard: skip until connected so the 5s auto-dismiss timer never starts while the
  // component is returning null (invisible). Otherwise the CTA "fires and expires" silently.
  useEffect(() => {
    if (!isConnected || showMessage) {
      return;
    }

    if (colors?.websocketData?.cta) {
      setBackendCta({
        text: colors.websocketData.cta,
        visible: true,
        isEaten: false,
        source: 'websocket'
      });
      return;
    }

    const urlKind = getFixedUrlChatCtaKind(pathname);
    if (urlKind) {
      if (!getUrlCtaAckForSource(urlKind)) {
        const bodyKey = URL_CTA_BODY_KEYS[urlKind];
        setBackendCta({
          text: bodyKey ? t(bodyKey) : '',
          visible: true,
          isEaten: false,
          source: urlKind
        });
        return;
      }
      /* URL matches a pattern but this fixed CTA was dismissed — do not fall back to welcome here */
    } else if (!getWelcomeAck() && !getWelcomeSessionShown()) {
      markWelcomeSessionShown();
      setBackendCta({
        text: t('welcomeChatCta'),
        visible: true,
        isEaten: false,
        source: 'welcome'
      });
      return;
    }

    // Scroll re-engagement: fires once per session after user scrolls 55%+ and no other CTA is showing.
    if (scrollEngageReady && !backendCta.visible) {
      setBackendCta({
        text: t('scrollEngageBody'),
        visible: true,
        isEaten: false,
        source: 'scroll-engage'
      });
      return;
    }

    setBackendCta(prev => {
      if (prev.source === 'websocket') {
        return { text: '', visible: false, isEaten: false, source: null };
      }
      if (prev.source?.startsWith('url-')) {
        const nextKind = getFixedUrlChatCtaKind(pathname);
        if (!nextKind || nextKind !== prev.source) {
          return { text: '', visible: false, isEaten: false, source: null };
        }
        if (getUrlCtaAckForSource(nextKind)) {
          return { text: '', visible: false, isEaten: false, source: null };
        }
      }
      return prev;
    });
  }, [isConnected, colors?.websocketData?.cta, showMessage, t, pathname, scrollEngageReady]);

  // Render nothing until the WebSocket is connected
  if (!isConnected) {
    return null;
  }

  return (
    <div className={`mia-toggle-chat-container ${isClosing ? 'mia-chat-closing' : ''}`}>
      <AnimatePresence mode="wait" onExitComplete={() => setIsAnimating(false)}>
        {isChatVisible && !isClosing && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ 
              opacity: 0, 
              y: isMobile ? 50 : 20, 
              scale: 0.95,
              transition: {
                duration: isMobile ? 0.2 : 0.4,
                ease: "easeInOut"
              }
            }}
            transition={{ 
              type: "tween",
              duration: isMobile ? 0.3 : 0.4, 
              ease: "easeOut"
            }}
            className="mia-chat-body"
            key="chat-body"
          >
            {/* ChatBody content would go here */}
          </motion.div>
        )}

        {/* Floating message - prioritize trigger notifications over backend CTA */}
        {(() => {
          // Checking render conditions
          return !isChatVisible && showMessage && !isAnimating;
        })() && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9, x: 15 }}
            animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
            exit={{ opacity: 0, y: 10, scale: 0.95, x: 10 }}
            transition={{ 
              duration: isMobile ? 0.3 : 0.4, 
              ease: [0.2, 0.9, 0.4, 1],
              opacity: { duration: isMobile ? 0.2 : 0.25 }
            }}
            key="floating-message"
            onClick={() => handleMessageClick(floatingMessages[currentMessageIndex])}
            className="mia-floating-message"
          >
            <button 
              className="mia-close-message-btn"
              onClick={handleCloseMessage}
              onTouchEnd={(e) => {
                e.preventDefault();
                handleCloseMessage(e);
              }}
              aria-label={t("close")}
            >
              <X size={16} />
            </button>
            
            <div className="mia-message-content">
              <div className="mia-message-text">
                {getDisplayMessage()}
              </div>
              <div className="mia-message-cta">
                {floatingMessages[currentMessageIndex]?.cta_label || t("openChat")}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Chat button - kept outside AnimatePresence to avoid remount/flicker */}
      {!isChatVisible && (
        <div className={`mia-chat-widget${isMobile && mobileFabOnly ? ' mia-chat-widget--fab-only' : ''}`}>
          {/* Backend CTA message - hide when notification is showing */}
          {backendCta.visible && !showMessage && (
            <motion.div 
              className={`mia-backend-cta ${isEnhancedFlutterCardSource(backendCta.source) ? 'mia-backend-cta--welcome' : ''} ${backendCta.isEaten ? 'mia-cta-eaten' : ''}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: backendCta.isEaten ? 0 : 1, 
                y: backendCta.isEaten ? 60 : 0,
                x: backendCta.isEaten ? 40 : 0,
                scale: backendCta.isEaten ? 0.5 : 1,
                rotate: backendCta.isEaten ? 10 : 0
              }}
              transition={{
                duration: 0.6,
                ease: "easeInOut"
              }}
              onClick={openBackendCtaChat}
            >
              <div className="mia-backend-cta-content">
                <span>{backendCta.text}</span>
                <button 
                  className="mia-close-cta-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (backendCta.source === 'welcome') {
                      acknowledgeWelcomeCta();
                    } else if (backendCta.source?.startsWith('url-')) {
                      acknowledgeUrlCtaBySource(backendCta.source);
                    }
                    setBackendCta(prev => ({ ...prev, visible: false }));
                  }}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="mia-backend-cta-action">
                <span className={isEnhancedFlutterCardSource(backendCta.source) ? 'mia-backend-cta-action-label' : undefined}>
                  {getFixedFlutterActionLabel(t, backendCta.source)}
                </span>
                <ChevronRight size={14} className="mia-cta-action-arrow" aria-hidden />
            </div>
            </motion.div>
          )}
          
          {/* Chat button container */}
          <div className={`mia-chat-button-container${isMobile && mobileFabOnly ? ' mia-chat-button-container--fab-only' : ''}`}>
          <button
              className="mia-chat-button"
            onClick={handleToggleClick}
          >
            <img 
              src="https://files.aitomotivelab.com/icona_aitomotivelab.svg"
              alt="AItomotive Lab"
            />
          </button>
            
            <button
              type="button"
              className={`mia-chat-cta${isNarrowViewport ? " mia-chat-cta--compact" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                collapseMobileChatta();
                dismissFixedCtaIfOpen();
                toggleChat(false);
              }}
              title={t("chatWithUs")}
              aria-label={t("chatWithUs")}
            >
              <MessageCircle
                size={isNarrowViewport ? 17 : 18}
                strokeWidth={2.5}
                className="mia-chat-icon"
              />
              <span className="mia-cta-text">
                {isNarrowViewport ? t("chatWithUsShort") : t("chatWithUs")}
              </span>
              <ChevronRight
                size={isNarrowViewport ? 14 : 16}
                strokeWidth={2.5}
                className="mia-cta-arrow"
                aria-hidden
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};



export default NewToggleChat;   
